import * as React from 'react';

import { Dispatch } from 'redux';
import { connect } from 'react-redux';

import { Translate } from 'react-localize-redux';

import AppState from '@webapp/store/state';
import { ProductSelected } from '@webapp/store/products/action-creators';

import { rgba } from 'polished';
import styled, { css } from 'styled-components';

import { Coupon } from '@services/product/types/coupon';
import { Product } from '@services/product/types/product';
import { PaymentFrequency } from '@services/product/types/payment-frequency';

import Flex from '@components/layout/flex';
import Typography from '@components/basics/typography';

// const BEGINNER = [
//   {
//     title: 'ARTIST_PROFILE.PLANS.BEGINNER.DISTRIBUTION.TITLE',
//     missed: 'ARTIST_PROFILE.PLANS.BEGINNER.DISTRIBUTION.MISSED',
//     text: 'ARTIST_PROFILE.PLANS.BEGINNER.DISTRIBUTION.TEXT',
//   },
//   {
//     title: 'ARTIST_PROFILE.PLANS.BEGINNER.RELEASE_TIME.TITLE',
//     missed: 'ARTIST_PROFILE.PLANS.BEGINNER.RELEASE_TIME.MISSED',
//     text: 'ARTIST_PROFILE.PLANS.BEGINNER.RELEASE_TIME.TEXT',
//   },
//   {
//     title: 'ARTIST_PROFILE.PLANS.BEGINNER.WITHDRAWALS.TITLE',
//     missed: 'ARTIST_PROFILE.PLANS.BEGINNER.WITHDRAWALS.MISSED',
//     text: 'ARTIST_PROFILE.PLANS.BEGINNER.WITHDRAWALS.TEXT',
//   },
// ];

interface PricingCardProps {
  className?: string;
  product: Product;
  activeCoupon?: Coupon;
  paymentFrequency: PaymentFrequency;
  isSelected: boolean;
  hasCurrentPlan?: boolean;
  isPlanExpiring?: boolean;
  onClick: () => void;
}

interface StateProps {
  isMobile: boolean;
  hasHighlight: boolean;
  isFree: boolean;
  productSelected: (
    product: Product,
    frequency: PaymentFrequency
  ) => ProductSelected;
}

const mapStateToProps = (
  state: AppState,
  props: PricingCardProps
): Omit<StateProps, 'productSelected'> => ({
  isMobile: state.ui.mobile,
  hasHighlight: props.product.name.toUpperCase() === 'GROWTH',
  isFree: props.product.name.toUpperCase() !== 'BEGINNER',
});

const mapDispatchToProps = (
  dispatch: Dispatch
): Pick<StateProps, 'productSelected'> => ({
  productSelected: (product: Product, frequency: PaymentFrequency) =>
    dispatch(new ProductSelected(product, frequency)),
});

const PricingCard: React.FC<PricingCardProps & StateProps> = ({
  hasCurrentPlan,
  isPlanExpiring,
  onClick,
  isSelected,
  ...props
}) => {
  // let isProPlan = props.product.name === 'Pro' ? true : false;
  // let isBeginnerPlan = props.product.name === 'Beginner' ? true : false;
  return (
    <div
      className={`${props.className} ${isSelected ? 'selected-plan' : ''}`}
      onClick={onClick}
    >
      <div className="header">
        <SelectedType type="title" weight="700" capitalize>
          {props.product.name === 'Label' ? 'Business' : props.product.name}
        </SelectedType>
        {!isPlanExpiring ? (
          <PositionedType
            type="small"
            weight="700"
            className={isSelected ? 'current-plan' : ''}
          >
            <Translate
              id={
                hasCurrentPlan
                  ? 'ARTIST_PROFILE.BILLING.PLAN.TITLE'
                  : 'ARTIST_PROFILE.BILLING.NEW_PLAN.TITLE'
              }
            />
          </PositionedType>
        ) : (
          <PositionedType
            type="small"
            weight="700"
            isPlanExpiring={isPlanExpiring}
          >
            <Translate id="ONBOARDING.PRICING.EXPIRING.BTN" />
          </PositionedType>
        )}
      </div>

      <StyledBox
        className={props.product.name === 'Beginner' ? 'beginner' : ''}
      >
        {props.product.name !== 'Beginner'
          ? props.product.features?.map((option, index) => (
              <FeaturesType
                key={`${props.product?.id}-${index}`}
                weight="400"
                type="small"
              >
                {option}
              </FeaturesType>
            ))
          : props.product.features?.map((item, i) => (
              <FeaturesType key={`${item}-${i}`} weight="400" type="small">
                {item}
              </FeaturesType>
            ))}
      </StyledBox>
    </div>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(styled(PricingCard)`
  padding: 30px;
  position: relative;
  background-color: ${({ theme }) => theme.colors.background};
  cursor: pointer;

  &:first-child {
    border-radius: 10px 10px 0 0;
  }

  &:last-child {
    border-radius: 0 0 10px 10px;
  }

  &:only-child {
    border-radius: 10px;
  }

  &.selected-plan {
    background-color: ${({ theme }) => theme.colors.primary};

    span {
      color: ${({ theme }) => theme.colors.background};
    }
  }

  .header {
    margin-bottom: 24px;
  }

  .beginner-items {
    margin-bottom: 10px;

    &:last-child {
      margin-bottom: 0;
    }

    .right-info {
      width: 60%;
      .right-info-text {
        text-align: right;
        @media ${({ theme }) => theme.device.tablet} {
          text-align: left;
        }
      }
    }
  }
`);

const StyledBox = styled(Flex)`
  flex-direction: column;

  &.beginner {
    .missed {
      color: ${({ theme }) => theme.colors.tertiary};
    }
  }
`;

const PositionedType = styled(Typography)<{ isPlanExpiring?: boolean }>`
  border-radius: 90px;
  padding: 4px 8px;
  max-width: 90px;
  transform: translate(-8px, 0);
  opacity: 1;
  text-align: center;
  position: absolute;
  top: 30px;
  right: 30px;
  background-color: ${({ theme }) => theme.colors.hint};
  color: ${({ theme }) => theme.colors.black} !important;

  &.current-plan {
    background-color: ${({ theme }) => theme.colors.greenLight};
  }

  ${({ isPlanExpiring }) =>
    isPlanExpiring
      ? css`
          background-color: ${({ theme }) => rgba(theme.colors.error, 0.15)};
          color: ${({ theme }) => theme.colors.error} !important;
          max-width: 70px;
        `
      : undefined}
`;

const FeaturesType = styled(Typography)`
  margin-bottom: 10px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SelectedType = styled(Typography)`
  color: ${({ theme }) => theme.colors.pureBlackWhite};
`;
